import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { Calibration } from 'common/types/valuation/valuation';
import { NoteData } from 'components/Notes/types';
import { getStringValue } from 'utilities';
import { Note } from '../../types';

interface SaveNotesOptions {
  onSuccess?: (savedNotes: NoteData[]) => void;
  onSettled?: () => void;
}

interface UseCalibrationNotesParams {
  calibrations?: Calibration[];
  notesSaveInFlightByPanelRef: { current: Set<string> };
  saveNotes: (notesData?: NoteData[], options?: SaveNotesOptions) => void;
}

export const useCalibrationNotes = (params: UseCalibrationNotesParams) => {
  const { calibrations, notesSaveInFlightByPanelRef, saveNotes } = params;
  const [notesInCalibration, setNotesInCalibration] = useState<Note[]>([]);

  const saveCalibrationNotes = useCallback(() => {
    if (isEmpty(notesInCalibration)) {
      return;
    }

    notesInCalibration.forEach(noteEntry => {
      const panelId = getStringValue(noteEntry?.panelId);
      const calibration = calibrations?.find(currentCalibration => currentCalibration?.panelId === panelId);

      if (
        !isNil(calibration?.id) &&
        noteEntry?.notes &&
        !isEmpty(noteEntry?.notes) &&
        noteEntry?.notesHasChanged &&
        !notesSaveInFlightByPanelRef.current.has(panelId)
      ) {
        notesSaveInFlightByPanelRef.current.add(panelId);

        saveNotes(noteEntry?.notes, {
          onSuccess: savedNotes => {
            setNotesInCalibration(previousNotes =>
              previousNotes.map(currentNote =>
                currentNote.panelId === noteEntry.panelId
                  ? {
                    ...currentNote,
                    notes: savedNotes,
                    notesHasChanged: false,
                  }
                  : currentNote
              )
            );
          },
          onSettled: () => {
            notesSaveInFlightByPanelRef.current.delete(panelId);
          },
        });
      }
    });
  }, [calibrations, notesInCalibration, notesSaveInFlightByPanelRef, saveNotes]);

  return {
    notesInCalibration,
    saveCalibrationNotes,
    setNotesInCalibration,
  };
};
